import type { ReactNode } from 'react'
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
// Aliased: this component's own handler is already called `signOut`, and it
// is the more meaningful name at the call site (it does the two things that
// actually sign this tab out; the import only makes the request).
import { signOut as signOutRequest } from '@/api/client'
import { useSession } from '@/auth/session'
import { Button } from '@/components/ui/button'
import { Toaster } from '@/components/ui/sonner'
import { cn } from '@/lib/cn'
import { useSkewWatch } from '@/pwa/skew'
import { landingFor } from './landing'
import { visibleNav } from './nav'
import { SyncTray } from './SyncTray'
import { UpdateBanner } from './UpdateBanner'
import { UpgradeRequired } from './UpgradeRequired'

/**
 * The chrome every future feature mounts into (plan 040 / architecture §8).
 * Renders a left `<nav>` on desktop and a bottom tab bar on phones — ONE
 * component, ONE `<nav>` element, restyled by breakpoint via Tailwind's
 * responsive classes, NOT two parallel trees for "mobile" vs "desktop". A
 * second tree would double the accessibility surface (two landmark navs, two
 * sets of link text a screen reader has to reconcile) and let the two drift
 * the moment one gets a new nav item and the other doesn't — the exact
 * failure the brief warns against. The switch is pure CSS: the same
 * `visibleNav(me)` list renders once; `md:` variants change layout
 * (`flex-row` bottom bar → `flex-col` left sidebar) and position, nothing
 * else.
 *
 * `isLoading` guard, VERIFIED not decorative: `useSession()`'s query is
 * in-flight for at least one render after mount — react-query never
 * resolves synchronously — during which `isSignedIn` is `false` because no
 * data has arrived yet, not because the visitor is signed out. Without this
 * guard, that transient `false` would render `<Navigate to="/login" />`
 * immediately, and NOTHING re-checks afterwards (`LoginRoute` never reads
 * `useSession()`), so the redirect sticks even for a genuinely signed-in
 * principal — the query resolving a moment later has nowhere to route back
 * to. Confirmed by hand: forcing `content = null` unconditionally (the
 * `isLoading` branch's own value) and re-running `pnpm test:web -- shell`
 * turns every one of the five tests red (each renders at `/` and needs the
 * real landing/nav to appear) — see the task report for the exact output.
 * Reverted after confirming.
 *
 * Redirect ordering: signed-out beats "which area", not the other way round
 * — an unauthenticated visitor hitting `/pipeline` directly should land on
 * `/login`, not have `landingFor` reasoned about for a `me` that doesn't
 * exist (`landingFor(undefined)` already returns `'/login'` too, but
 * checking `isSignedIn` first keeps the `/` special-case below from ever
 * having to consider the signed-out case at all).
 *
 * `<UpdateBanner />` is rendered OUTSIDE and ABOVE every branch below —
 * task 16 review round 1's CRITICAL, fixed here. The previous shape used a
 * sequence of early `return`s (`if (isLoading) return null`, `if
 * (!isSignedIn) return <Navigate .../>`, `if (upgradeRequired) return
 * <UpgradeRequired />`, ...), with `<UpdateBanner />` mounted only in the
 * FINAL, "everything checked out" return. `UpdateBanner` — and so the
 * entire update mechanism, `useUpdatePrompt`'s polling, device-mode
 * auto-accept, all of it — was therefore UNREACHABLE whenever any earlier
 * branch fired. MEASURED as the worst case this produces: a `kiosk`
 * principal below the client-skew floor (`upgradeRequired` true) with a
 * worker genuinely waiting sat for a simulated four hours with ZERO
 * `SKIP_WAITING` posts and ZERO `registration.update()` polls — exactly
 * the permanently-stale wall tablet review §5.6 names as this whole
 * feature's motivation, reached through the very lever
 * (`min_client_build`) its own doc comments cite, because the one screen
 * that state renders (`UpgradeRequired`) offers only a plain
 * `window.location.reload()` that does NOT activate a waiting worker (this
 * task's own hand-run drill measured that: a reload while a worker waits
 * re-serves the OLD build from the SW's own precache). Fixed by computing
 * WHICH content to show as a plain variable (`content`, below) instead of
 * returning early, and wrapping `<UpdateBanner />` and `content` together
 * in one shared container at the bottom (a `dvh` flex column since
 * web-pre-deploy item 12b — see that fix's own comment on the `return`
 * below) — so `UpdateBanner` mounts, and `useUpdatePrompt`'s polling and
 * device-mode auto-accept run, in EVERY branch this component can render,
 * including behind `UpgradeRequired`.
 * `test/update-prompt.test.tsx`'s "still runs the device-mode accept path
 * while the below-floor screen is showing" is the covering test; deleting
 * this hoist (moving `<UpdateBanner />` back inside only the final branch)
 * is the mutation it catches. THAT SENTENCE IS THE CORRECTED ONE, and the
 * correction is named here because a commit message cannot be amended:
 * `0cd113c` asserted "skew-appshell.test.tsx still covers it" of this same
 * hoist, which is false — that file asserts only the `UpgradeRequired`
 * heading, never the banner, so reverting the hoist leaves it green.
 * Measured and disclosed in `8d772c5`'s own message ("A CORRECTION THIS
 * COMMIT OWNS"), which is where the full transcript lives.
 *
 * NOT hoisted all the way to the router root (`App.tsx`) instead, which
 * would additionally cover `/login`/`/set-password`/`/styleguide`/the `*`
 * 404 route — those sit OUTSIDE `AppShell` entirely (`routes.tsx`'s
 * top-level siblings), and `test/render.tsx`'s `renderApp` mounts `routes`
 * directly, never `App.tsx`, so a fix living only in `App.tsx` would be
 * invisible to this whole test suite. Those routes are also lower-stakes
 * for THIS fix's purpose: none of them has a principal, so "device mode"
 * cannot even apply, and the CRITICAL's measured worst case — a signed-in
 * principal stuck behind a full-screen blocking state with genuinely no
 * accept path — cannot arise there. Left as a disclosed residual, not
 * silently declared closed.
 */
export function AppShell() {
  const { me, isLoading, isSignedIn } = useSession()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // Task 15: `pwa/skew.ts`'s own top comment names THIS component as "the
  // shell" its store is written for. Called unconditionally, before any
  // early return, same as every other hook in this component — the boot
  // check it starts on mount must run regardless of which branch below ends
  // up rendering.
  const upgradeRequired = useSkewWatch()

  const items = visibleNav(me)

  /**
   * `POST /api/auth/sign-out` EXISTS and revokes the platform session since
   * 2026-08-04 (decision ledger, "In flight now"; `packages/api/test/
   * sign-out.workers.test.ts` pins it) — until then this comment correctly
   * recorded that no server route backed this action and the httpOnly
   * cookie outlived every "Sign out". The request stays best-effort —
   * `api/client.ts`'s `signOut` (imported as `signOutRequest`, leaving this
   * handler its name) never throws and never reports whether anything
   * answered — because a site tab may be offline at the moment its user
   * signs out, and the two lines after it are what change what THIS tab
   * believes either way.
   *
   * `setQueryData(['session', 'me'], null)`, NOT `invalidateQueries` —
   * review round 1's Important 1, MEASURED (test/shell.test.tsx's sign-out
   * test): `invalidateQueries` only marks the query stale and, with no
   * observer mounted, refetches nothing — and whenever the sign-out POST
   * did not land (offline, a fault), the server-side session is still live,
   * so any later refetch would simply re-cache the signed-in principal.
   * `setQueryData(null)` overwrites the cache immediately and synchronously
   * with the same `null` sentinel `useSession`'s 401 branch uses, and
   * `staleTime: Infinity` keeps that `null` from being silently refetched —
   * on a shared device, User A's "Sign out" must stop User B's Back button
   * from resurrecting the shell even when the network was down.
   */
  const signOut = async () => {
    await signOutRequest()
    queryClient.setQueryData(['session', 'me'], null)
    navigate('/login')
  }

  // WHICH content to show, as a plain variable rather than a sequence of
  // early `return`s — see this file's own top comment ("`<UpdateBanner />`
  // is rendered OUTSIDE and ABOVE every branch below") for why: an early
  // `return` here would skip the `<UpdateBanner />` mount below entirely,
  // which is exactly the review round 1 CRITICAL this shape closes.
  let content: ReactNode
  if (isLoading) {
    content = null
  } else if (!isSignedIn) {
    content = <Navigate to="/login" replace />
  } else if (upgradeRequired) {
    // Checked BEFORE the `/` redirect below (and before any nav/content
    // renders): a below-floor signed-in visitor sees the upgrade screen
    // regardless of which URL sent them here, rather than being routed to
    // a landing area first that would then also need this same check.
    content = <UpgradeRequired />
  } else if (location.pathname === '/') {
    content = <Navigate to={landingFor(me)} replace />
  } else {
    content = (
      <div data-app-frame className="flex flex-1 flex-col md:flex-row">
        {/*
          No `md:h-screen`: this row is already `flex-1` inside the
          `min-h-dvh` column below, so the sidebar stretches to it. Claiming
          a full `100vh` here put a second viewport under a banner that had
          already used part of one — 849px against 800, measured by
          `e2e/responsive-shell.spec.ts`'s banner test.
        */}
        <nav
          aria-label="Primary"
          className="order-2 flex shrink-0 justify-around border-t border-border bg-background md:order-1 md:w-56 md:flex-col md:justify-start md:gap-1 md:border-t-0 md:border-r md:p-4"
        >
          {items.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                cn(
                  // `min-w-0` + the truncating span: flex items refuse to
                  // shrink below min-content by default, so with enough
                  // areas granted the bar simply grew past the screen and
                  // dragged the whole page sideways — found on a real phone
                  // (2026-08-03), pinned by `e2e/responsive-shell.spec.ts`'s
                  // widest-principal test (413px measured against a 375px
                  // viewport before this). Labels ellipsize when crowded
                  // rather than forcing overflow; a friendlier treatment for
                  // the many-capability case (a "More" sheet) is the owner's
                  // deferred UX polish, not this fix.
                  'flex min-w-0 flex-1 flex-col items-center gap-1 px-2 py-[var(--gf-row-y)] text-center text-[length:var(--gf-text)] text-muted-foreground hover:text-foreground md:flex-none md:flex-row md:justify-start md:rounded-md md:px-3 md:hover:bg-accent',
                  isActive && 'text-foreground md:bg-accent',
                )
              }
            >
              <span className="max-w-full truncate">{item.label}</span>
            </NavLink>
          ))}
          {/*
            Task 18: one Sync entry inside this SAME responsive `<nav>` —
            not a second copy for "sidebar" vs "bottom bar". `SyncTray`'s
            own top comment has the full reasoning; this is what makes
            "present in both" true without this file growing a second nav
            tree.
          */}
          <SyncTray />
        </nav>
        <div className="order-1 flex flex-1 flex-col md:order-2">
          <header className="flex items-center justify-between border-b border-border p-4">
            <span className="text-sm text-muted-foreground">{me?.label ?? me?.kind}</span>
            <Button variant="outline" onClick={signOut}>
              Sign out
            </Button>
          </header>
          <main className="flex-1 p-4">
            <Outlet />
          </main>
        </div>
      </div>
    )
  }

  return (
    // web-pre-deploy item 12b: ONE flex column sized to the DYNAMIC
    // viewport (`dvh`, not `vh` — a mobile browser's chrome resizes the
    // VISUAL viewport at runtime, the same class of bug one layer down),
    // so `<UpdateBanner />` and `content` share a single box whose total
    // height is capped at the viewport rather than each contributing a
    // full one. Previously `<UpdateBanner />` and `content` were fragment
    // siblings with NO shared container at all, and `content`'s own inner
    // frame (below) claimed `min-h-screen` — a MINIMUM of a full 100vh on
    // top of whatever the banner already used, so showing the banner
    // pushed the total past one viewport and shoved the `order-2` bottom
    // tab bar (a phone's ONLY navigation) below the fold. `content`'s frame
    // now claims `flex-1` instead (see below) — its height comes FROM this
    // shared column's remaining space, not on top of it. This changes the
    // CONTAINER only; the hoist itself (`<UpdateBanner />` outside and
    // above every branch) is unchanged — see this file's own top comment.
    <div className="flex min-h-dvh flex-col">
      {/*
       * Rendered UNCONDITIONALLY — before `content`, and regardless of
       * which branch above computed it. `UpdateBanner` itself decides
       * whether it has anything to show (browser mode with a waiting
       * update) or do silently (device mode), and renders `null`
       * otherwise; this is what makes it reachable behind `UpgradeRequired`
       * too (see this file's own top comment for the measured CRITICAL
       * this closes).
       */}
      <UpdateBanner />
      {/*
       * Task 18: the FIRST real mount of the vendored `Toaster` anywhere
       * outside `/styleguide` — a drain-time refusal (`outbox/drain.ts`'s
       * `announceRefusal`) calls `sonner`'s `toast()` directly, from
       * non-React code, and that call is a courtesy only if SOMETHING is
       * mounted to render it when it fires. Unconditional, same reasoning
       * as `<UpdateBanner />` above: a toast firing while `UpgradeRequired`
       * or the loading branch is showing should still be seen.
       * `closeButton`: the task brief's own behaviour ("the tray entry
       * survives the toast being dismissed") needs a real, clickable way to
       * dismiss ONE toast without waiting out its auto-dismiss timer —
       * sonner renders no close affordance at all unless asked for one.
       * `theme="light"`, not the vendored wrapper's own `next-themes`-driven
       * default (`'system'` with no `<ThemeProvider>` mounted anywhere in
       * this app): `theme/tokens.css`'s own top comment is explicit that
       * GridFit has no dark mode at all ("`color-scheme: light` ... pinned
       * ... so those don't go dark to match a visitor's OS"), so letting
       * this ONE component guess a system preference the rest of the app
       * already neutralises would be a real inconsistency, not only a test
       * gap — MEASURED as one, though: sonner's own `theme === 'system'`
       * path calls `window.matchMedia`, which jsdom does not implement,
       * throwing inside `<Toaster>` and tripping every `renderApp` test's
       * `ErrorBoundary` the moment this component mounted unthemed (see the
       * task report for the failing output this produced before this prop
       * was added).
       */}
      <Toaster closeButton theme="light" />
      {content}
    </div>
  )
}

/**
 * `/no-areas` (brief step 4). NOT a `features/*` folder: it isn't a real
 * area gated by one of `NAV_ITEMS`'s capabilities, it's the shell's own
 * fallback for a principal holding none of the capabilities `landing.ts`'s
 * `PRIORITY` table checks — kept here, next to the redirect logic that
 * sends a principal here, rather than invented as a sixth feature folder
 * the brief's Files list never asked for.
 */
export function NoAreasScreen() {
  return (
    <div className="space-y-2">
      <h1 className="text-xl font-semibold">No areas available</h1>
      <p>No areas are available to this account. Ask an administrator to check your permissions.</p>
    </div>
  )
}

/**
 * `routes.tsx`'s top-level `{ path: '*' }` catch-all (review round 1's
 * Important 3). Measured before this existed: `/does-not-exist` rendered
 * react-router's own DEVELOPMENT error boundary ("Unexpected Application
 * Error! ... 💿 Hey developer 👋") — a debugging aid meant for local
 * development, not a screen this app should ever let a real visitor see, and
 * exactly the failure mode `landing.ts`'s own `/portal` gap (also fixed this
 * round) produced before `nav.ts`/`routes.tsx` gained a matching area.
 *
 * Deliberately `element`, not `errorElement`: this component handles an
 * UNMATCHED URL (a routing decision made before any component ever renders),
 * which is a different failure from a MOUNTED component throwing. DONE, task
 * 12: `shell/ErrorBoundary.tsx`'s `ErrorBoundary` (a class component),
 * `RouteErrorFallback` (wired via `errorElement`) and `routes.tsx`'s own
 * `lazyArea` helper now cover that other failure, and a third case
 * (`lazy` rejecting) that measurement showed belongs to neither of the
 * first two. See `routes.tsx`'s own top comment for the full, measured
 * three-way split and how each was verified; this route stays untouched
 * (`element` only, no `errorElement`, no `lazy`) because it renders static
 * content with nothing in it that can throw or fail to load.
 *
 * Placed at the TOP LEVEL in `routes.tsx` (a sibling of `/login`, not a
 * child of `AppShell`) so it catches an unmatched path REGARDLESS of sign-in
 * state — a signed-out visitor mistyping a URL gets the same honest answer
 * as a signed-in one, with no dependency on `useSession()` ever resolving.
 */
export function NotFoundScreen() {
  return (
    <div className="mx-auto flex min-h-screen max-w-sm flex-col items-center justify-center gap-2 p-6 text-center">
      <h1 className="text-xl font-semibold">Page not found</h1>
      <p className="text-sm text-muted-foreground">There&rsquo;s nothing at this address.</p>
      <Link to="/" className="text-sm underline">
        Back to GridFit
      </Link>
    </div>
  )
}
